// The chrome dock-in (R182, mockup round ten, "Magnet Snap"): what the DOM
// overlay does while the map arrives.
//
// The screen is black from the moment the way in closes until the map spawn
// starts — no water, no panels, no minimap. The spawn then owns the screen
// for its own run, with the chrome still outside the viewport. When the
// spawn enters its last stretch (`LATE_LEAD_SEC` in render/spawn.ts — most
// of the map is revealed by then), every panel travels to its dock inside
// one second.
//
// **How it spends that second is a theme value** (R826, themes.md §3.4).
// The World body's `dock` field names one of five arrivals, and each one
// states its own clock, curve and stagger in
// `[presentation.dockin.<id>]`. The AUTO value is Magnet Snap: one start,
// a curve that carries a panel a few pixels past its dock line and lets
// it settle back, and a border flash as it lands. The other four are
// Hard Dock, Cascade, Pressure Rise and Surface Wake. Every value spends
// the SAME second, because the season banner goes up on the frame the
// arrival starts and must not stand over a chrome still on its way in.
//
// Three parties move together, and only one of them is DOM:
//
//  - **The panels are CSS.** This module sets classes on `#ui` and nothing
//    else. Every distance and every curve is in `ui/styles.css`, beside the
//    layout rule it has to agree with, because a panel's travel is a fact
//    about where that panel sits. The map buttons travel with them
//    (R1747), and state their distance the same way: `--rest` and `--out`,
//    never a bare `transform`.
//  - **The minimap is on the canvas**, so it cannot be a CSS transition.
//    This module publishes `progress` — the same eased 0..1 the panels are
//    on — and `fast/minimap.ts` turns it into an offset of its own. The curve is sampled here, once, rather than guessed twice: an
//    eased approach and a back-out approach diverge most in the last 150 ms,
//    which is exactly where a mismatch shows.
//  - **The black is one fixed div**, `#blackout`, which `index.html` paints
//    before any script runs.
//
// The arrival plays on `login` and on `season`. A `resync` is not an arrival
// — the player is already looking at that world — and it never touches the
// chrome.
//
// **The black must never be a trap.** A client that never reaches a spawn
// would otherwise sit at a black screen with no way out, so `abort()` puts
// everything in its docked state at once, and a watchdog calls `abort()` for
// the case nobody remembered.
//
// **The black wears the launch lockup** (brand.md §9, §11): from `cover()`
// the black shows the picture of the launch image that fits the viewport,
// a still, because a world loads faster than the sting could play. The
// cover draws the lockup on a canvas at the device pixels of the screen
// (`coverLockupCanvas`) and does not scale the raster, so the lockup is
// large and sharp. It appears when the lockup face is loaded and never
// holds the arrival. The sting plays at the start of a match instead
// (`ui/stingcover.ts`). The black fades with the lockup on it, and
// `abort()` takes both away. A page with no black (the theme preview)
// shows no lockup.

import { clamp01 } from '../render/math';
import { hiddenDocument, reducedMotion, skipOnInput, stopSkipOnInput } from '../render/motion';
import { DOCK_MS, DOCKIN, PALETTE_TOKENS } from '../gen/presentation';
import type { DockInStyle } from '../gen/presentation';
import { AUTO_CHROME_LOOK } from './chromelook';
import type { DockStyle } from './settings';
import { coverLockupCanvas, loadLockupFace } from '../brandart/lockupdraw';
import { devBrand } from './sign';

/** The whole arrival, in ms. **Every value spends the same second**
 *  (R826): a value may stagger the panels inside it, but the last one
 *  lands on this figure, and balancegen refuses a table that does not. */
export { DOCK_MS };

/** The key of the chart in a value's `delayMs`. The chart is drawn on the
 *  canvas, so it is the one arrival the stylesheet cannot carry. */
const CHART = 'minimap';

/** The ground of every launch image: the ink of `[presentation.palette]`
 *  (`paintLaunch` of `brandart/paint.ts`). The cover fills the screen
 *  with it under the lockup. */
const LAUNCH_INK = `rgb(${PALETTE_TOKENS['--ink-rgb']})`;

/** The figures of one value, with AUTO for an id this build does not
 *  carry — the fallback `chromeLookOf` makes, one step later. */
function figuresOf(id: DockStyle): DockInStyle {
  return DOCKIN[id] ?? DOCKIN[AUTO_CHROME_LOOK.dock];
}

/** The sampled curve of each value. A bezier costs a Newton walk to
 *  build, and the preview steps through every value in one session, so
 *  the walk happens once per value and not once per arrival. */
const eases = new Map<string, (k: number) => number>();

function easeOf(id: DockStyle): (k: number) => number {
  let f = eases.get(id);
  if (!f) {
    f = cubicBezier(...figuresOf(id).bezier);
    eases.set(id, f);
  }
  return f;
}

/** How long the black may stand with no spawn behind it before the client
 *  gives up and shows the chrome anyway. */
export const COVER_MAX_MS = 15000;

/** The back-out curve of the AUTO value. Neither this module nor
 *  `ui/styles.css` states it any more: the sheet reads `--dock-ease`, and
 *  both figures come from `[presentation.dockin.magnet]` (R826). */
export const SNAP_BEZIER: readonly [number, number, number, number] =
  DOCKIN[AUTO_CHROME_LOOK.dock].bezier;

/**
 * A CSS `cubic-bezier(x1, y1, x2, y2)` as a function of the fraction of the
 * duration. `y` leaves the 0..1 range wherever the curve does, which is the
 * point of a back-out: the overshoot is the animation.
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): (k: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const xOf = (t: number) => ((ax * t + bx) * t + cx) * t;
  const dxOf = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  const yOf = (t: number) => ((ay * t + by) * t + cy) * t;
  return (k: number) => {
    if (k <= 0) return 0;
    if (k >= 1) return 1;
    // x is monotone in t for every curve CSS accepts, so Newton converges in
    // three or four steps across the whole range the animation samples.
    let t = k;
    for (let i = 0; i < 8; i++) {
      const err = xOf(t) - k;
      if (Math.abs(err) < 1e-6) return yOf(t);
      const slope = dxOf(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= err / slope;
    }
    // Bisection for the ends, where the derivative can vanish.
    let lo = 0, hi = 1;
    t = k;
    for (let i = 0; i < 24; i++) {
      const x = xOf(t);
      if (Math.abs(x - k) < 1e-6) break;
      if (x < k) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return yOf(t);
  };
}

/** The sampled Magnet Snap curve: the AUTO value's, built once and kept
 *  in the cache every arrival of that value reads. */
export const snapEase = easeOf(AUTO_CHROME_LOOK.dock);

/**
 * The flash of contact, as the CSS `dock-snap` keyframes state it: nothing
 * for the first 62% of the travel, brightest at 72%, gone by the end.
 *
 * It takes the linear fraction of the duration and not the eased offset,
 * because the back-out curve passes its dock line twice and would otherwise
 * light the border on the way out as well as on the way in.
 */
export function snapFlash(k: number): number {
  if (k <= 0.62 || k >= 1) return 0;
  if (k < 0.72) return (k - 0.62) / 0.1;
  return 1 - (k - 0.72) / 0.28;
}

/**
 * Where the arrival is.
 *
 * - `idle` — the chrome is docked and nothing is playing.
 * - `covered` — black over everything, chrome outside the viewport.
 * - `revealing` — the spawn has the screen, chrome still outside.
 * - `docking` — the one-second arrival is running.
 */
export type DockPhase = 'idle' | 'covered' | 'revealing' | 'docking';

/**
 * The classes this module sets on `#ui`. They are the whole interface to
 * `ui/styles.css`.
 *
 * - `docking` — every dockable panel sits at its `--out` transform.
 * - `snapping` — the border flash runs.
 * - `nodock` — no transition and no animation, for one frame, so a state
 *   change lands without travel.
 */
export class ChromeDockIn {
  /** The CHART's eased fraction of the travel, 0 outside the viewport and
   *  1 docked. The minimap follows this, because it cannot follow the
   *  CSS. It is the chart's own clock and not the panels' (R826): a value
   *  may start the chart late and give it a shorter travel, and only the
   *  two together land it with the panels. */
  progress = 1;

  private phase: DockPhase = 'idle';
  private startedAt = 0;
  private watchdog = 0;
  /** The lockup canvas on the black, and the count of the covers, so a
   *  face load that ends after the cover went paints nothing. */
  private lockup: HTMLCanvasElement | null = null;
  private covers = 0;
  private lockupOn = false;

  /** Which of the dock-in looks the arrival plays (themes.md §3.4). */
  private styleId: DockStyle;

  constructor(
    private readonly ui: HTMLElement,
    private readonly black: HTMLElement | null,
    style: DockStyle = AUTO_CHROME_LOOK.dock,
    private readonly reduced: () => boolean = reducedMotion,
    /** Wall time. The panels are CSS transitions, and a CSS transition runs
     *  on wall time whatever the frame loop is doing, so the chart has to as
     *  well: accumulating frame deltas here would let a stalled frame put
     *  the chart behind the panels it is supposed to land with. */
    private readonly now: () => number = () => performance.now(),
  ) {
    this.styleId = style;
  }

  /** The look in force. */
  get style(): DockStyle { return this.styleId; }

  /** The figures of the look in force. */
  private get figures(): DockInStyle { return figuresOf(this.styleId); }

  /**
   * Sets the look. A change while an arrival runs is REFUSED, because the
   * travel of a panel is a CSS transition that is already on its way: a
   * class written in the middle of it would move the panels and leave the
   * chart, which follows `progress`, behind. The game writes the look one
   * time, before the first `cover`, and the preview writes it between two
   * loops of its own.
   */
  setStyle(id: DockStyle): void {
    if (this.phase !== 'idle') return;
    this.styleId = id;
  }

  /** For the frame loop, which must not pay for a tick that does nothing. */
  get running(): boolean { return this.phase === 'docking'; }

  /** Presentation-only; the tests and the browser check read it. */
  get state(): DockPhase { return this.phase; }

  /** The linear fraction of the travel, 0..1. The flash reads this; the
   *  positions read `progress`, which is the same clock eased. */
  get clock(): number {
    if (this.phase !== 'docking') return 1;
    return Math.min(1, (this.now() - this.startedAt) / DOCK_MS);
  }

  /**
   * The flash of contact, 0..1, for the chart's own frame.
   *
   * Only the AUTO value clamps on, so every other value answers 0 here
   * and the stylesheet drops the `dock-snap` animation for the same
   * reason. The figure is the value's own `flash`, never a name.
   */
  get flash(): number {
    if (this.phase !== 'docking' || !this.figures.flash) return 0;
    return snapFlash(this.clock);
  }

  /**
   * The sonar ring Surface Wake opens under the chart, 0..1 (R826).
   *
   * It is a third clock: the panels travel, the chart follows 180 ms
   * behind them, and the ring opens later still. A value with no ring
   * answers 0, and `fast/minimap.ts` then draws nothing.
   */
  get ring(): number {
    const f = this.figures;
    if (this.phase !== 'docking' || f.ringMs <= 0) return 0;
    return clamp01((this.clock * DOCK_MS - f.ringDelayMs) / f.ringMs);
  }

  /**
   * Black over everything, and the chrome outside the viewport. The snap out
   * carries no transition, because it happens under the black: a season roll
   * would otherwise show the panels leaving before the world ends.
   */
  cover() {
    stopSkipOnInput(this.skipper);
    this.clearWatchdog();
    this.phase = 'covered';
    this.progress = 0;
    if (this.black) {
      this.black.style.display = 'block';
      this.black.style.opacity = '1';
      this.wearLaunchLockup(this.black);
    }
    this.jump(() => {
      this.ui.classList.add('docking');
      this.ui.classList.remove('snapping');
    });
    this.watchdog = window.setTimeout(() => this.abort(), COVER_MAX_MS);
  }

  /** The spawn has the screen now. The black goes; the chrome stays out. */
  reveal() {
    if (this.phase !== 'covered') return;
    this.phase = 'revealing';
    // The watchdog stays armed. It guards the whole way to a docked chrome,
    // not only the black: a spawn that starts and never ends would leave the
    // panels outside the viewport, which is the same trap one step later.
    if (this.black) this.black.style.opacity = '0';
  }

  /** The spawn is in its last stretch, or has ended. One second, and the
   *  chrome is home. */
  dock() {
    if (this.phase === 'idle' || this.phase === 'docking') return;
    // A hidden document runs no CSS transition, and this clock is wall time,
    // so an arrival started in a background tab would finish here while the
    // panels stood still. Nobody is watching it, so there is nothing to
    // play: everything docks at once and the session starts square.
    if (hiddenDocument()) { this.abort(); return; }
    this.clearWatchdog();
    this.hideBlack();
    this.phase = 'docking';
    this.startedAt = this.now();
    this.progress = 0;
    this.ui.classList.remove('docking');
    this.ui.classList.add('snapping');
    // Skippable, exactly as the spawn is (R163). This plays on every login,
    // and a player who wants the chrome now must be able to have it.
    skipOnInput(this.skipper);
  }

  /** Advances the arrival. Cheap and safe when nothing is playing. */
  tick() {
    if (this.phase !== 'docking') return;
    const k = this.clock;
    const f = this.figures;
    // The chart has a clock of its own: it may start after the panels and
    // it may travel for a different length, and only the two together put
    // it on the dock line at the same instant they land. Cascade starts
    // it 240 ms late over 760 ms; Surface Wake starts it 180 ms late over
    // 820 ms; both land on DOCK_MS.
    const kc = clamp01((k * DOCK_MS - (f.delayMs[CHART] ?? 0)) / f.chartMs);
    // Reduced motion has no travel to ease: the panels fade in place, and
    // the chart moves at a constant rate, so the two still land together.
    this.progress = this.reduced() ? kc : easeOf(this.styleId)(kc);
    if (k >= 1) this.settle(false);
  }

  /** Jumps to the docked state. Any pointerdown or keydown calls this. */
  skip() { if (this.phase === 'docking') this.settle(true); }

  private readonly skipper = () => this.skip();

  /**
   * Everything docked at once, with no travel and no black: the way out of
   * every path that never reaches a spawn — a fatal error, a server that
   * does not answer, a hello that never comes.
   */
  abort() {
    this.clearWatchdog();
    stopSkipOnInput(this.skipper);
    this.hideBlack();
    this.phase = 'idle';
    this.progress = 1;
    this.jump(() => this.ui.classList.remove('docking', 'snapping'));
  }

  /** For a teardown of the whole client. */
  destroy() {
    this.clearWatchdog();
    stopSkipOnInput(this.skipper);
  }

  /** True while the black wears the launch lockup. The tests read it. */
  get lockupShown(): boolean { return this.lockupOn; }

  /** @param jumped true when a skip cut the travel short, and the panels
   *  have to be put home without finishing the transition they are on. */
  private settle(jumped: boolean) {
    stopSkipOnInput(this.skipper);
    this.phase = 'idle';
    this.progress = 1;
    this.startedAt = 0;
    if (jumped) this.jump(() => {});
    this.ui.classList.remove('snapping');
  }

  /**
   * Changes the classes with no travel: `nodock` stands while `change`
   * runs and through one reflow, so the state lands at once and the
   * transition between the two states never starts.
   */
  private jump(change: () => void) {
    this.ui.classList.add('nodock');
    change();
    void this.ui.offsetHeight;
    this.ui.classList.remove('nodock');
  }

  private hideBlack() {
    if (!this.black) return;
    this.black.style.opacity = '0';
    this.black.style.display = 'none';
    this.black.style.backgroundImage = '';
    this.black.style.backgroundColor = '';
    this.dropLockup();
  }

  private dropLockup() {
    this.covers++;
    this.lockupOn = false;
    this.lockup?.remove();
    this.lockup = null;
  }

  /** Puts the launch lockup on the black: the ink, and the lockup of
   *  `coverLockupCanvas` once its face is loaded, with the DEV line on a
   *  page of the dev identity (brand.md §15). */
  private wearLaunchLockup(black: HTMLElement) {
    this.dropLockup();
    this.lockupOn = true;
    black.style.backgroundImage = 'none';
    black.style.backgroundColor = LAUNCH_INK;
    const cover = this.covers;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dpr = window.devicePixelRatio || 1;
    const dev = devBrand();
    loadLockupFace().then(() => {
      if (cover !== this.covers) return;
      const c = coverLockupCanvas(vw, vh, dpr, dev);
      if (!c) return;
      this.lockup = c;
      black.appendChild(c);
    }, () => { /* no face, no lockup: the cover stays the ink */ });
  }

  private clearWatchdog() {
    if (this.watchdog) { clearTimeout(this.watchdog); this.watchdog = 0; }
  }
}
